import type { TokenPayload } from "@/types";
import { localStore, sessionStore } from "@/lib/webStorage";
import { jwtDecode } from "jwt-decode";

const AUTH_TOKEN_KEY = "auth_token";
/** Tab-local impersonation access JWT. Durable admin session stays in localStorage. */
const IMPERSONATION_ACCESS_TOKEN_KEY = "auth_token_impersonation";
const ACCESS_TOKEN_FRESHNESS_BUFFER_MS = 10_000;

/** Blocks TanStack Query retry after impersonation expiry restore, before the hard navigation unloads. */
let impersonationEnded = false;

export const decodeToken = (token: string): TokenPayload | null => {
  try {
    return jwtDecode<TokenPayload>(token);
  } catch {
    return null;
  }
};

export const isImpersonationPayload = (
  payload: Pick<TokenPayload, "impersonator_sub"> | null,
): boolean => Boolean(payload?.impersonator_sub);

export const getStoredToken = (): string | null => {
  return localStore.getItem(AUTH_TOKEN_KEY);
};

export const setStoredToken = (token: string): void => {
  localStore.setItem(AUTH_TOKEN_KEY, token);
};

export const removeStoredToken = (): void => {
  localStore.removeItem(AUTH_TOKEN_KEY);
};

export const getImpersonationAccessToken = (): string | null => {
  return sessionStore.getItem(IMPERSONATION_ACCESS_TOKEN_KEY);
};

export const setImpersonationAccessToken = (token: string): void => {
  impersonationEnded = false;
  sessionStore.setItem(IMPERSONATION_ACCESS_TOKEN_KEY, token);
};

export const clearImpersonationAccessToken = (): void => {
  sessionStore.removeItem(IMPERSONATION_ACCESS_TOKEN_KEY);
};

/** Bearer for API calls: this tab's impersonation JWT, otherwise the shared session. */
export const getActiveAccessToken = (): string | null => {
  return getImpersonationAccessToken() ?? getStoredToken();
};

export const isAccessTokenFresh = (token: string): boolean => {
  const payload = decodeToken(token);
  if (payload?.exp === undefined) {
    return false;
  }
  return payload.exp * 1000 > Date.now() + ACCESS_TOKEN_FRESHNESS_BUFFER_MS;
};

export const isImpersonationAccessFresh = (): boolean => {
  const token = getImpersonationAccessToken();
  return token !== null && isAccessTokenFresh(token);
};

/** Impersonation JWT expired: restore admin and do not let Query replay the request as admin. */
export const markImpersonationEnded = (): void => {
  impersonationEnded = true;
};

export const shouldBlockQueryRetry = (): boolean => {
  return getImpersonationAccessToken() !== null || impersonationEnded;
};
